import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  signal,
  computed,
  inject,
  Injector,
  ChangeDetectorRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  Router,
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
} from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";

import { RestProviderService } from "../../../services/rest-provider/rest-provider.service";
import { RequestModel } from "../../../models/request/request-model";
import { NavigationRequestModel } from "../../../models/navigation/navigation-request-model";
import { NavigationResponseModel } from "../../../models/navigation/navigation-response-model";
import { NavigationNodeSerializer } from "../../../models/serializing/navigation-node-serializer";
import { DynamicLoadingUtil } from "../../../shared/helpers/dynamic-loading-util";
import { TranslationsProviderService } from "../../../services/translations-provider/translations-provider.service";
import { ActionsService } from "../../../services/actions-service/actions.service";
import { LocalStorageService } from "../../../services/local-storage-service/local-storage.service";
import { UserService } from "../../../services/user/user-provider.service";
import { UtilityService } from "../../../services/utility/utility.service";
import { DynamicFieldsManagerService } from "../../../services/dynamic-fields/dynamic-fields-manager.service";
import { SemUserProfileDialogComponent } from "../../../components/controls/dialogs/sem-user-profile-dialog/sem-user-profile-dialog.component";
import { SemDailyChallengeDialogComponent } from "../../../components/controls/dialogs/sem-daily-challenge-dialog/sem-daily-challenge-dialog.component";
import { DialogType } from "../../../shared/components/sem-dynamic/sem-dynamic.component";
import { environment } from "src/environments/environment";

interface BreadcrumbItem {
  title: string;
  url: string;
}

@Component({
  selector: "app-wg-header",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./wg-header.component.html",
  styleUrls: ["./wg-header.component.css"],
})
export class WgHeaderComponent implements OnInit, OnDestroy {
  @Input() viewTitle: string = "";
  @Input() formTitle: string = "";

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private injector = inject(Injector);
  private changeDet = inject(ChangeDetectorRef);
  public restProvider = inject(RestProviderService);
  public translationsService = inject(TranslationsProviderService);
  public actionsService = inject(ActionsService);
  private localStorageService = inject(LocalStorageService);
  private userService = inject(UserService);
  private utility = inject(UtilityService);
  private dfms = inject(DynamicFieldsManagerService);
  private dialog = inject(MatDialog);

  private routerSub: Subscription;
  private routesLoadedSub: Subscription;
  private formTitleSub: Subscription;
  private navStartSub: Subscription;
  private isFormPage = false;

  // Menu state
  userMenuState = false;
  languageMenuState = false;

  // API data
  userMenuResult: NavigationResponseModel[] = [];
  languageMenuResult: NavigationResponseModel[] = [];
  customNodes: NavigationResponseModel[] = [];
  childrenNodes: Record<string, { Show: boolean }> = {};
  formActionResponses: any[] = [];

  // Breadcrumbs
  breadcrumbs = signal<BreadcrumbItem[]>([]);
  private lastKnownListingCrumb: { title: string; url: string } | null = null;

  // Config
  version: string = environment.version;
  showOnlyUser: boolean = environment.showOnlyUserInHeader;
  showPlatformIcon = false;
  showNotificationDotIcon = true;
  currentLanguage: string = "";
  // Project-specific optional setting: not every project's environment.ts declares it,
  // so it is read defensively instead of typed on the environment object.
  instructionsUrl: string = (environment as any).instructionsUrl || "";

  // User info (computed from restProvider.currentUser)
  get username(): string {
    return (
      this.restProvider.currentUser?.FullName ||
      this.restProvider.currentUser?.Username ||
      "User"
    );
  }

  // Second line under the name: the login name, when the first line shows the full name
  get loginName(): string {
    const user = this.restProvider.currentUser;
    return user?.FullName && user?.Username && user.Username !== user.FullName ? user.Username : "";
  }

  get initials(): string {
    const name = this.username;
    if (!name) return "U";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
      ).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  get isDashboard(): boolean {
    return window.location.href.includes("dashboard");
  }

  ngOnInit() {
    // Load menu data (same API calls as SemHeaderMenuComponent)
    this.loadMenuItems("user-menu");
    this.loadMenuItems("language-menu");
    this.loadMenuItems("custom-header-menu");
    this.loadMenuItems("form-action-value");

    // Language
    let languageCode = this.localStorageService.get("language");
    if (languageCode == null) {
      languageCode = environment.language;
    }
    this.currentLanguage = languageCode.toUpperCase();

    // Platform icon check
    this.checkPlatformRights();

    // Breadcrumbs: save the listing crumb before we leave it (still on its URL at NavigationStart)
    this.navStartSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        if (!this.isFormPage) {
          const currentCrumbs = this.breadcrumbs();
          if (currentCrumbs.length > 0) {
            this.lastKnownListingCrumb = {
              title: currentCrumbs[currentCrumbs.length - 1].title,
              url: this.router.url.substring(1),
            };
          }
        }
      });

    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.isFormPage = false;
        this.formTitle = "";
        // Also clear the BehaviorSubject so that if wg-header is recreated
        // (routeReuseStrategy=false rebuilds the component tree on every nav),
        // the new instance's formTitleSub doesn't immediately get a stale title.
        this.dfms._formTitleChanged.next("");
        this.loadBreadcrumb();
      });

    this.routesLoadedSub = this.dfms.routesLoaded.subscribe((data: any) => {
      if (data && typeof data === "object" && Object.keys(data).length > 0) {
        this.loadBreadcrumb();
      }
    });

    this.formTitleSub = this.dfms.formTitleChanged.subscribe(
      (title: string) => {
        this.formTitle = title || "";
        if (title) this.isFormPage = true;
        this.loadBreadcrumb();
      },
    );
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
    this.routesLoadedSub?.unsubscribe();
    this.formTitleSub?.unsubscribe();
    this.navStartSub?.unsubscribe();
    this.dfms._formTitleChanged.next("");
  }

  // --- Data loading (same as SemHeaderMenuComponent) ---

  private loadMenuItems(type: string) {
    const model = new RequestModel();
    model.url = "/api/navigation/getNodes";
    const request = new NavigationRequestModel();
    request.context = type;
    request.depth = 0;
    request.rootNode = null;
    model.data = request;

    this.restProvider.serializer = new NavigationNodeSerializer();

    let cacheData: string | null = null;
    if (environment.useRouteAndMasterpageCache) {
      cacheData = this.localStorageService.get(
        "/api/navigation/getNodes_" + type,
      );
    }

    if (cacheData) {
      this.processMenuData(type, JSON.parse(cacheData));
    } else {
      this.restProvider
        .getDataPOST<NavigationResponseModel[]>(model)
        .subscribe((data) => {
          this.localStorageService.add(
            "/api/navigation/getNodes_" + type,
            JSON.stringify(data),
          );
          this.processMenuData(type, data);
        });
    }
  }

  private processMenuData(type: string, data: any[]) {
    if (type === "language-menu") {
      this.languageMenuResult = data;
    } else if (type === "user-menu") {
      this.userMenuResult = data;
    } else if (type === "custom-header-menu") {
      this.customNodes = data;
      for (let i = 0; i < this.customNodes.length; i++) {
        if (
          this.customNodes[i].Children != null &&
          this.customNodes[i].Children.length > 0
        ) {
          this.childrenNodes[this.customNodes[i].Id + ""] = { Show: false };
        }
      }
      this.checkNotificationData();
    } else if (type === "form-action-value") {
      try {
        for (let i = 0; i < data.length; i++) {
          this.actionsService.callAction(
            data[i]["NavigationNodeRule"]["FormAction"],
            {},
            null,
            this.formActionValueComplete.bind(this),
            this,
            false,
            null,
          );
        }
      } catch (e) {}
    }
  }

  private checkNotificationData() {
    this.restProvider
      .getDataGET("api/task/checkunopened", "application/json")
      .subscribe((data: any) => {
        if (data != null && typeof data != "undefined" && data == "true") {
          this.showNotificationDotIcon = true;
          if (this.customNodes) {
            for (let i = 0; i < this.customNodes.length; i++) {
              if (this.customNodes[i].Icon == "notification-center-icon") {
                this.customNodes[i].Icon = "notification-center-dot-icon";
              }
            }
          }
        }
      });
  }

  private formActionValueComplete(value: any) {
    this.formActionResponses.push(value);
  }

  // --- Platform rights check ---

  private checkPlatformRights() {
    const userStringJson = this.localStorageService.get("user");
    if (!userStringJson) return;
    try {
      const user = JSON.parse(userStringJson);
      if (!user) return;

      if (user.Groups && user.Groups.length > 0) {
        user.Groups.forEach((group: any) => {
          if (group && group.Rights && group.Rights.length > 0) {
            const platformRight = group.Rights.find(
              (x: any) => x.Right.Id == 7,
            );
            if (platformRight) {
              this.showPlatformIcon = !platformRight.Deny;
            }
          }
        });
      }

      if (user.Rights && user.Rights.length > 0) {
        const platformRight = user.Rights.find((x: any) => x.Right.Id == 7);
        if (platformRight) {
          this.showPlatformIcon = !platformRight.Deny;
        }
      }
    } catch {}
  }

  // --- Breadcrumb logic (from SemBreadcrumbComponent) ---

  private loadBreadcrumb() {
    let crumbs: BreadcrumbItem[] = [];

    const navigationNodeFrom = DynamicLoadingUtil.currentRoute
      ? JSON.parse(JSON.stringify(DynamicLoadingUtil.currentRoute))
      : null;
    DynamicLoadingUtil.currentRoute = null;

    const url = this.router.url.substring(1);

    // Try finding breadcrumb from previous route
    if (navigationNodeFrom && Object.keys(navigationNodeFrom).length > 1) {
      this.visitNode(navigationNodeFrom);
    }

    if (DynamicLoadingUtil.currentRoute == null) {
      for (let i = 0; i < DynamicLoadingUtil.navigationNodes.length; i++) {
        if (DynamicLoadingUtil.currentRoute == null) {
          this.visitNode(DynamicLoadingUtil.navigationNodes[i]);
        } else {
          break;
        }
      }
    }

    // Try without view in url
    if (DynamicLoadingUtil.currentRoute == null) {
      for (let i = 0; i < DynamicLoadingUtil.navigationNodes.length; i++) {
        if (DynamicLoadingUtil.currentRoute == null) {
          this.visitNode(DynamicLoadingUtil.navigationNodes[i], true);
        } else {
          break;
        }
      }
    }

    if (DynamicLoadingUtil.currentRoute == null && navigationNodeFrom) {
      if (Object.keys(navigationNodeFrom).length > 1) {
        DynamicLoadingUtil.currentRoute = navigationNodeFrom;
      }
    }

    if (DynamicLoadingUtil.currentRoute) {
      const bc: any[] = DynamicLoadingUtil.currentRoute["BreadCrumbs"];
      if (bc && bc.length > 0) {
        crumbs = bc
          .filter((b: any) => b.Url !== "cms/dashboard")
          .map((b: any) => ({
            title: this.translateKey(b.Title),
            url: b.Url || "",
          }));
        if (
          crumbs.length > 0 &&
          crumbs[crumbs.length - 1].title ===
            this.translationsService.translate("AddNew")
        ) {
          crumbs.pop();
        }
      } else {
        // Node matched but has no BreadCrumbs — use the node's own title
        const title =
          DynamicLoadingUtil.currentRoute["OriginalTitle"] ||
          DynamicLoadingUtil.currentRoute["Title"];
        if (title) {
          crumbs.push({
            title: this.translateKey(title),
            url: DynamicLoadingUtil.currentRoute["Url"] || "",
          });
        }
      }
    }

    // --- Form / detail page detection ---

    const isDetailPage = /[?&]id=/.test(url);

    // Strips only the id param, preserving other query params (e.g. ?model=).
    const formBaseUrl = url.includes("?id=")
      ? url.split("?id=")[0]
      : url.replace(/&id=[^&]*/, "");

    // Path without any query params — used to search navigation nodes.
    const currentPath = url.split("?")[0];

    // Search nav nodes directly for a listing that owns this form (reliable, no
    // timing dependency).  The listing URL always encodes its form path as `url=<path>`.
    const listingNodeForForm = this.findListingNodeForForm(currentPath);

    // isFormPage is set by formTitleSub whenever the form component emits a title.
    const isFormOrDetailPage =
      isDetailPage || this.isFormPage || !!listingNodeForForm;

    // Drop stale formTitle on pages that are not forms at all.
    if (!isFormOrDetailPage) {
      this.formTitle = "";
    }

    if (isFormOrDetailPage) {
      // When the matched nav node IS the form page itself, its own crumb is the last
      // BreadCrumb item — remove it so the listing becomes the navigable back-link.
      const currentNodeUrl = DynamicLoadingUtil.currentRoute
        ? DynamicLoadingUtil.currentRoute["Url"] || ""
        : "";
      if (crumbs.length > 0 && currentNodeUrl === formBaseUrl) {
        crumbs.pop();
      }

      // Inject the listing crumb when BreadCrumbs didn't provide one.
      if (crumbs.length === 0) {
        const listingCrumb =
          listingNodeForForm ??
          (this.lastKnownListingCrumb?.url ? this.lastKnownListingCrumb : null);
        if (listingCrumb?.url) {
          crumbs.push({ title: listingCrumb.title, url: listingCrumb.url });
        }
      }

      // Append formTitle as the non-clickable current-page crumb (url = '').
      if (this.formTitle) {
        crumbs.push({ title: this.formTitle, url: "" });
      }
    }

    // Fallback: scan nav nodes directly (catches listing pages missed by visitNode)
    if (crumbs.length === 0) {
      const urlToMatch = url.includes("?model") ? url : url.split("?")[0];
      for (const node of DynamicLoadingUtil.navigationNodes) {
        if (
          node.Url &&
          (node.Url === urlToMatch ||
            node.Url.endsWith(urlToMatch) ||
            node.Url.includes(urlToMatch + "&"))
        ) {
          const title = node.OriginalTitle || node.Title;
          crumbs.push({ title: this.translateKey(title), url: node.Url });
          break;
        }
      }
    }

    // Clear breadcrumbs on dashboard
    if (url.includes("/dashboard") || url.includes("dashboard")) {
      crumbs = [];
    }

    // For non-form pages (listings) mark the last crumb as the current page by
    // clearing its URL → renders as non-clickable <span>, preventing a self-link.
    if (!isFormOrDetailPage && crumbs.length > 0) {
      crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], url: "" };
    }

    this.breadcrumbs.set(crumbs);
    this.changeDet.detectChanges();
  }

  // Finds the listing navigation node that owns the given form path.
  // Listing URLs encode their form path as the `url=` query parameter, e.g.
  // cms/list?model=…&url=cms/myform&view=1 — we match on that.
  private findListingNodeForForm(
    formPath: string,
  ): { title: string; url: string } | null {
    if (!formPath || !DynamicLoadingUtil.navigationNodes?.length) return null;
    const search = (nodes: any[]): { title: string; url: string } | null => {
      for (const node of nodes) {
        if (node?.Url) {
          const u: string = node.Url;
          if (
            u.includes("url=" + formPath + "&") ||
            u.endsWith("url=" + formPath)
          ) {
            return {
              title: this.translateKey(node.OriginalTitle || node.Title || ""),
              url: u,
            };
          }
        }
        if (node?.Children?.length) {
          const hit = search(node.Children);
          if (hit) return hit;
        }
      }
      return null;
    };
    return search(DynamicLoadingUtil.navigationNodes);
  }

  private visitNode(node: any, withoutViewUrl: boolean = false) {
    let goToUrl: string = this.router.url.substring(1);

    if (withoutViewUrl && goToUrl.includes("&view=")) {
      goToUrl = goToUrl.substring(0, goToUrl.indexOf("&view="));
    }

    if (goToUrl.includes("?id=")) {
      goToUrl = goToUrl.split("?id=")[0];
    }

    if (!goToUrl.includes("?model")) {
      goToUrl = goToUrl.split("?")[0];
    }

    let nodeUrl: string = node.Url || "";
    if (withoutViewUrl && nodeUrl.includes("&view=")) {
      nodeUrl = nodeUrl.substring(0, nodeUrl.indexOf("&view="));
    }
    if (
      nodeUrl &&
      (nodeUrl === goToUrl ||
        nodeUrl.endsWith(goToUrl) ||
        nodeUrl.includes(goToUrl + "&"))
    ) {
      DynamicLoadingUtil.currentRoute = node;
      return;
    }

    if (node.Children != null && typeof node.Children != "undefined") {
      for (let i = 0; i < node.Children.length; i++) {
        const child = node.Children[i];
        if (!child) continue;

        let nodeChildUrl: string = child.Url || "";

        if (withoutViewUrl && nodeChildUrl.includes("&view=")) {
          const ind = nodeChildUrl.indexOf("&view=");
          nodeChildUrl = nodeChildUrl.substring(0, ind);
        }

        if (
          nodeChildUrl &&
          (nodeChildUrl === goToUrl ||
            nodeChildUrl.endsWith(goToUrl) ||
            nodeChildUrl.includes(goToUrl + "&"))
        ) {
          DynamicLoadingUtil.currentRoute = child;
          return;
        } else {
          this.visitNode(child, withoutViewUrl);
        }
      }
    }
  }

  private translateKey(key: string): string {
    if (!key) return "";
    const translationKey = key.includes(".") ? key.split(".")[1] : key;
    return this.translationsService.translate(translationKey);
  }

  // --- Navigation ---

  navigateToDashboard() {
    this.router.navigateByUrl("/cms/dashboard");
  }

  navigateBreadcrumb(url: string) {
    if (url && url.length > 0) {
      this.utility.processNavigationUrl(url);
    }
  }

  // --- Custom header node actions ---

  topMenuItemClick(item: any) {
    if (item != null && item.Url != null && item.Url.length > 1) {
      window.location.href = item.Url;
    } else if (item?.NavigationNodeRule?.ActionBody) {
      eval(item.NavigationNodeRule.ActionBody);
    }
  }

  openChildrenNodes(node: any) {
    const keys = Object.keys(this.childrenNodes);
    for (let i = 0; i < keys.length; i++) {
      if (keys[i] != "" + node.Id) {
        this.childrenNodes[keys[i]].Show = false;
      }
    }
    this.childrenNodes[node.Id].Show = !this.childrenNodes[node.Id].Show;
  }

  // --- User menu ---

  userMenuClick() {
    this.userMenuState = !this.userMenuState;
    this.languageMenuState = false;
  }

  userPopupClick() {
    const dialogRef = this.dialog.open(SemUserProfileDialogComponent, {
      data: { type: DialogType.Confirmation, text: "WebGalis_NavigateAway" },
      panelClass: "wg-user-profile-dialog",
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  openInstructions() {
    if (this.instructionsUrl) {
      window.open(this.instructionsUrl, "_blank");
    }
  }

  openDailyChallengeDialog() {
    this.userMenuState = false;
    this.dialog.open(SemDailyChallengeDialogComponent, {
      panelClass: "wg-daily-challenge-dialog",
    });
  }

  // --- Language ---

  languageMenuClick() {
    this.languageMenuState = !this.languageMenuState;
    this.userMenuState = false;
  }

  languageChange(language: string) {
    const code = language.toLowerCase();
    const token = this.localStorageService.get("token");
    const user = this.localStorageService.get("user");
    this.localStorageService.clear();
    this.localStorageService.add("token", token);
    this.localStorageService.add("user", user);
    this.localStorageService.add("language", code);

    const transObj = new RequestModel();
    transObj.data = { LanguageCode: code };
    transObj.url = "api/translation/all";

    this.restProvider.getDataPOST(transObj).subscribe((data: any) => {
      const translations = data["Translations"];
      this.translationsService.translations = translations;
      this.localStorageService.add(
        "translations",
        JSON.stringify(translations),
      );
      const currentUrl = this.router.url;
      this.router.resetConfig([]);
      window.location.href = currentUrl;
    });
  }

  // --- Other actions ---

  notificationsClick() {
    window.location.href = "cms/notificationcenter";
  }

  navigateToPlatform() {
    window.location.href = "platform";
  }

  // --- Icon mapping for custom header nodes ---

  mapCustomIcon(icon: string | null): string {
    if (!icon) return "more_horiz";
    const lower = icon.toLowerCase();
    if (lower.includes("notification")) return "notifications";
    if (lower.includes("search")) return "search";
    if (lower.includes("help") || lower.includes("support"))
      return "help_outline";
    if (lower.includes("settings") || lower.includes("cog")) return "settings";
    if (lower.includes("platform")) return "apps";
    return "more_horiz";
  }

  hasNotificationDot(icon: string | null): boolean {
    return icon != null && icon.toLowerCase().includes("dot");
  }

  // Close menus on outside click
  closeMenus() {
    this.userMenuState = false;
    this.languageMenuState = false;
    // Close all children nodes
    for (const key of Object.keys(this.childrenNodes)) {
      this.childrenNodes[key].Show = false;
    }
  }
}
